import { useState, useEffect, useRef, useCallback } from "react";
import { MapPin, Loader2, Check } from "lucide-react";
import api from "@/lib/api";

/**
 * Live address autocomplete.
 * - FR: api-adresse.data.gouv.fr (via backend proxy)
 * - Others (BE+): Photon/Nominatim fallback (via backend proxy)
 *
 * Props:
 *   country: ISO code (FR, BE…)
 *   value: current address string
 *   onChange(value): called when the user types freely
 *   onPick({street, city, postal_code, country, lat, lng}): called when a suggestion is selected
 *   verified: boolean — show a green check when an address has been validated
 *   error: optional error message
 *   testid: optional data-testid prefix
 */
export default function AddressAutocomplete({
  country = "FR",
  value = "",
  onChange,
  onPick,
  verified = false,
  error = "",
  placeholder = "12 rue de la Lumière",
  testid = "address",
}) {
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [highlight, setHighlight] = useState(-1);
  const debounceRef = useRef(null);
  const wrapRef = useRef(null);
  const cancelTokenRef = useRef(0);

  const fetchSuggestions = useCallback(
    async (q) => {
      if (!q || q.length < 3) {
        setSuggestions([]);
        return;
      }
      const token = ++cancelTokenRef.current;
      setLoading(true);
      try {
        const { data } = await api.get("/address/suggest", { params: { q, country } });
        if (token === cancelTokenRef.current) {
          setSuggestions(data.results || []);
        }
      } catch (e) {
        if (token === cancelTokenRef.current) setSuggestions([]);
      } finally {
        if (token === cancelTokenRef.current) setLoading(false);
      }
    },
    [country]
  );

  // Debounced fetch
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!open) return;
    debounceRef.current = setTimeout(() => fetchSuggestions(value), 300);
    return () => clearTimeout(debounceRef.current);
  }, [value, fetchSuggestions, open]);

  // Close on outside click
  useEffect(() => {
    const onClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const handleSelect = (s) => {
    onPick?.(s);
    setOpen(false);
    setHighlight(-1);
  };

  const handleKey = (e) => {
    if (!open || suggestions.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlight((h) => Math.min(h + 1, suggestions.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === "Enter" && highlight >= 0) {
      e.preventDefault();
      handleSelect(suggestions[highlight]);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  return (
    <div ref={wrapRef} className="relative">
      <div className={`flex items-center gap-2 border rounded-sm bg-white h-12 px-3 transition-colors ${
        error ? "border-red-500" : verified ? "border-green-600" : "border-stone focus-within:border-graphite"
      }`}>
        <MapPin className={`h-4 w-4 shrink-0 ${verified ? "text-green-600" : "text-ash"}`} strokeWidth={1.5} />
        <input
          data-testid={`${testid}-input`}
          type="text"
          value={value}
          onChange={(e) => { onChange?.(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onKeyDown={handleKey}
          placeholder={placeholder}
          className="flex-1 bg-transparent outline-none text-sm"
          autoComplete="off"
        />
        {loading && <Loader2 className="h-4 w-4 text-ash animate-spin" />}
        {!loading && verified && <Check className="h-4 w-4 text-green-600" />}
      </div>
      {error && <p data-testid={`${testid}-error`} className="text-xs text-red-600 mt-1">{error}</p>}

      {open && suggestions.length > 0 && (
        <ul
          data-testid={`${testid}-list`}
          className="absolute z-30 left-0 right-0 mt-1 bg-white border border-stone rounded-sm shadow-lg max-h-72 overflow-y-auto"
        >
          {suggestions.map((s, idx) => (
            <li
              key={`${s.label}-${idx}`}
              onMouseDown={(e) => { e.preventDefault(); handleSelect(s); }}
              onMouseEnter={() => setHighlight(idx)}
              data-testid={`${testid}-suggestion-${idx}`}
              className={`px-3 py-2.5 cursor-pointer flex items-start gap-2 border-b border-sand last:border-0 ${
                highlight === idx ? "bg-sand" : "hover:bg-sand/60"
              }`}
            >
              <MapPin className="h-3.5 w-3.5 text-ash mt-0.5 shrink-0" />
              <div className="text-sm">
                <p className="text-ink">{s.street || s.label}</p>
                <p className="text-xs text-ash">{[s.postal_code, s.city, s.country].filter(Boolean).join(" · ")}</p>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
